import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Briefcase,
  Building2,
  CalendarDays,
  Globe,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { KpiCard, KpiCardSkeleton, kpiSnapshotBadge } from '@/features/dashboard/components/KpiCard'
import { useIacrmPlatformBusinesses } from '../hooks'
import { formatAppDate } from '@/lib/locale'

function statusBadgeClass(status: string) {
  switch (status) {
    case 'active':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20'
    case 'inactive':
      return 'bg-muted/60 text-muted-foreground border-transparent'
    case 'suspended':
      return 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20'
    default:
      return 'bg-muted/60 text-muted-foreground border-transparent'
  }
}

function PlatformIacrmSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <KpiCardSkeleton key={index} />
        ))}
      </div>
      <Skeleton className="h-80 w-full rounded-xl" />
    </div>
  )
}

export function PlatformIacrmPage() {
  const { t } = useTranslation()
  const query = useIacrmPlatformBusinesses()
  const businesses = query.data?.data ?? []
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null)

  const selectedBusiness = useMemo(
    () => businesses.find((business) => business.iacrm_id === selectedBusinessId) ?? businesses[0] ?? null,
    [businesses, selectedBusinessId],
  )

  const summary = businesses.reduce(
    (accumulator, business) => {
      accumulator.total += 1
      accumulator.services += business.services_count
      accumulator.clients += business.clients_count
      accumulator.pipeline += business.pipeline_count
      return accumulator
    },
    { total: 0, services: 0, clients: 0, pipeline: 0 },
  )

  if (query.isPending) {
    return <PlatformIacrmSkeleton />
  }

  if (query.isError) {
    return (
      <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
        {t('iacrm.platform.error')}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title={t('iacrm.platform.summary.businesses')}
          value={String(summary.total)}
          description={t('iacrm.platform.table.title', { count: summary.total })}
          icon={Building2}
          tone="primary"
          badge={kpiSnapshotBadge(t('iacrm.platform.summary.businesses'))}
        />
        <KpiCard
          title={t('iacrm.platform.summary.services')}
          value={String(summary.services)}
          description={t('iacrm.platform.summary.services')}
          icon={Briefcase}
          tone="info"
          badge={kpiSnapshotBadge(t('iacrm.platform.summary.services'))}
        />
        <KpiCard
          title={t('iacrm.platform.summary.clients')}
          value={String(summary.clients)}
          description={t('iacrm.platform.summary.clients')}
          icon={Users}
          tone="success"
          badge={kpiSnapshotBadge(t('iacrm.platform.summary.clients'))}
        />
        <KpiCard
          title={t('iacrm.platform.summary.pipeline')}
          value={String(summary.pipeline)}
          description={t('iacrm.platform.summary.pipeline')}
          icon={TrendingUp}
          tone="warning"
          badge={kpiSnapshotBadge(t('iacrm.platform.summary.pipeline'))}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
        {/* Business table */}
        <div className="rounded-xl bg-card">
          <div className="px-6 py-5">
            <h3 className="text-base font-semibold text-foreground">
              {t('iacrm.platform.table.title', { count: businesses.length })}
            </h3>
          </div>

          {businesses.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <Globe className="size-9 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">{t('iacrm.platform.table.emptyTitle')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('iacrm.platform.table.emptyDescription')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('iacrm.platform.table.columns.business')}</TableHead>
                  <TableHead>{t('iacrm.platform.table.columns.industry')}</TableHead>
                  <TableHead>{t('iacrm.platform.table.columns.country')}</TableHead>
                  <TableHead>{t('iacrm.platform.table.columns.status')}</TableHead>
                  <TableHead>{t('iacrm.platform.table.columns.createdAt')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.map((business) => {
                  const isSelected = business.iacrm_id === selectedBusiness?.iacrm_id
                  return (
                    <TableRow
                      key={business.iacrm_id}
                      className={isSelected ? 'bg-muted/30' : 'cursor-pointer'}
                      onClick={() => setSelectedBusinessId(business.iacrm_id)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{business.display_name}</p>
                          <p className="text-xs text-muted-foreground">{business.legal_name}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {business.industry ?? '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {business.country_code}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusBadgeClass(business.status)}>
                          {t(`iacrm.platform.status.${business.status}`, { defaultValue: business.status })}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatAppDate(business.created_at)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Detail panel */}
        <div className="rounded-xl bg-card p-6">
          <h3 className="text-base font-semibold text-foreground">
            {selectedBusiness?.display_name ?? t('iacrm.platform.detail.noneSelected')}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('iacrm.platform.detail.readOnlyHint')}</p>

          {selectedBusiness ? (
            <div className="mt-6 space-y-5">
              <div>
                <p className="text-xs font-medium text-muted-foreground">{t('iacrm.platform.detail.fields.legalName')}</p>
                <p className="mt-0.5 text-sm font-medium text-foreground">{selectedBusiness.legal_name}</p>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t('iacrm.platform.detail.fields.iacrmId')}</p>
                  <p className="mt-0.5 break-all font-mono text-xs text-foreground">{selectedBusiness.iacrm_id}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t('iacrm.platform.detail.fields.status')}</p>
                  <div className="mt-0.5">
                    <Badge variant="outline" className={statusBadgeClass(selectedBusiness.status)}>
                      {t(`iacrm.platform.status.${selectedBusiness.status}`, { defaultValue: selectedBusiness.status })}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t('iacrm.platform.detail.fields.industry')}</p>
                  <p className="mt-0.5 text-sm text-foreground">{selectedBusiness.industry ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground">{t('iacrm.platform.detail.fields.country')}</p>
                  <p className="mt-0.5 text-sm text-foreground">{selectedBusiness.country_code}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('iacrm.platform.detail.fields.services')}</p>
                  <p className="mt-0.5 text-lg font-semibold text-foreground">{selectedBusiness.services_count}</p>
                </div>
                <div className="rounded-lg bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('iacrm.platform.detail.fields.clients')}</p>
                  <p className="mt-0.5 text-lg font-semibold text-foreground">{selectedBusiness.clients_count}</p>
                </div>
                <div className="rounded-lg bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{t('iacrm.platform.detail.fields.pipeline')}</p>
                  <p className="mt-0.5 text-lg font-semibold text-foreground">{selectedBusiness.pipeline_count}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <CalendarDays className="size-3.5" />
                <span>{t('iacrm.platform.detail.fields.createdAt')} {formatAppDate(selectedBusiness.created_at)}</span>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-lg bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
              {t('iacrm.platform.detail.selectPrompt')}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
